import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { CURRENCY_OPTIONS, currencySymbol, formatInCurrency } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'm4l.preferredCurrency';
const SUPPORTED = new Set(CURRENCY_OPTIONS.map(c => c.value));

const CurrencyContext = createContext(null);

/** Detect a reasonable default currency from the browser's locale. */
function detectBrowserCurrency() {
    try {
        const nav = typeof navigator !== 'undefined' ? navigator : null;
        const locales = [nav?.language, ...(nav?.languages || [])].filter(Boolean);
        for (const loc of locales) {
            try {
                const parts = new Intl.NumberFormat(loc, { style: 'currency', currency: 'EUR' })
                    .resolvedOptions();
                // Walking a locale-driven map is more reliable than parsing region codes
                const region = (loc.includes('-') ? loc.split('-')[1] : loc).toUpperCase();
                const regionToCurrency = {
                    GB: 'gbp', UK: 'gbp',
                    US: 'usd',
                    CH: 'chf',
                    CA: 'cad',
                    AU: 'aud',
                    FR: 'eur', DE: 'eur', ES: 'eur', IT: 'eur', NL: 'eur', BE: 'eur',
                    IE: 'eur', PT: 'eur', AT: 'eur', FI: 'eur', GR: 'eur', LU: 'eur',
                };
                if (regionToCurrency[region]) return regionToCurrency[region];
            } catch {
                // ignore and try the next locale
            }
        }
    } catch {
        // noop
    }
    return 'eur';
}

export function CurrencyProvider({ children }) {
    const { user, isAuthenticated } = useAuth();
    const [rates, setRates] = useState({ EUR: 1 });
    const [fetchedAt, setFetchedAt] = useState(null);
    const [preferred, setPreferred] = useState(() => {
        if (typeof window === 'undefined') return 'eur';
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED.has(stored)) return stored;
        return detectBrowserCurrency();
    });

    // Load FX rates once on mount
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_URL}/api/currencies/rates`)
            .then(({ data }) => {
                if (cancelled) return;
                setRates(data?.rates || { EUR: 1 });
                setFetchedAt(data?.fetched_at || null);
            })
            .catch(() => { if (!cancelled) setRates({ EUR: 1 }); });
        return () => { cancelled = true; };
    }, []);

    // Logged-in users: profile currency takes precedence; also persist a change
    // back to the account so it syncs across devices.
    useEffect(() => {
        if (isAuthenticated && user?.preferred_currency && SUPPORTED.has(user.preferred_currency)) {
            setPreferred(user.preferred_currency);
            try { window.localStorage.setItem(STORAGE_KEY, user.preferred_currency); } catch { /* noop */ }
        }
    }, [isAuthenticated, user?.preferred_currency]);

    const changePreferred = useCallback(async (next) => {
        if (!next || !SUPPORTED.has(next)) return;
        setPreferred(next);
        try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
        if (isAuthenticated) {
            try {
                await axios.put(
                    `${API_URL}/api/profile`,
                    { preferred_currency: next },
                    { withCredentials: true }
                );
            } catch { /* best-effort sync; UI already updated */ }
        }
    }, [isAuthenticated]);

    /** Convert an amount from one currency to another using EUR as pivot. */
    const convert = useCallback((amount, fromCurrency, toCurrency) => {
        const n = Number(amount);
        if (!Number.isFinite(n)) return 0;
        const from = (fromCurrency || 'eur').toUpperCase();
        const to = (toCurrency || preferred || 'eur').toUpperCase();
        if (from === to) return n;
        const fromRate = rates[from];
        const toRate = rates[to];
        if (!fromRate || !toRate) return n; // No rate? Show original rather than lie.
        // Rates are EUR-based: 1 EUR = rates[CODE] CODE.
        // So: amount_in_EUR = amount / rates[FROM]; then * rates[TO]
        return (n / fromRate) * toRate;
    }, [rates, preferred]);

    /** Returns {display, original, hasConversion}. */
    const priceDisplay = useCallback((amount, originalCurrency) => {
        const from = (originalCurrency || 'eur').toLowerCase();
        const to = (preferred || 'eur').toLowerCase();
        if (from === to) {
            return {
                display: formatInCurrency(amount, from),
                original: formatInCurrency(amount, from),
                hasConversion: false,
            };
        }
        const converted = convert(amount, from, to);
        return {
            display: formatInCurrency(converted, to),
            original: formatInCurrency(amount, from),
            hasConversion: true,
        };
    }, [preferred, convert]);

    const value = useMemo(() => ({
        preferred,
        setPreferred: changePreferred,
        rates,
        fetchedAt,
        convert,
        priceDisplay,
        symbol: currencySymbol(preferred),
    }), [preferred, changePreferred, rates, fetchedAt, convert, priceDisplay]);

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        // Fallback for callers rendered outside the provider (shouldn't happen
        // in practice — all pages are wrapped). Behaves as identity formatter.
        return {
            preferred: 'eur',
            setPreferred: () => {},
            rates: { EUR: 1 },
            fetchedAt: null,
            convert: (a) => a,
            priceDisplay: (a, c) => ({
                display: formatInCurrency(a, c || 'eur'),
                original: formatInCurrency(a, c || 'eur'),
                hasConversion: false,
            }),
            symbol: '€',
        };
    }
    return ctx;
}
